/**
 * Tiny typed fetch client for the UAIP dashboard API (Fastify @ :5174).
 * Requests go through Vite's /api proxy so no absolute URLs in UI code.
 *
 * Types are duplicated from `apps/api/src/index.ts` intentionally — the web
 * app has no runtime dependency on Node code. Keep the two in sync.
 */

export interface CrawlSummary {
  id: string;
  path: string;
  targetUrl: string;
  framework: "java" | "python" | "unknown";
  pageCount: number;
  elementCount: number;
  crawledAt: string;
  durationMs: number;
  authed: boolean;
}

export interface FileInfo {
  name: string;
  size: number;
  language: string;
}

// Minimal CrawlResult shape. Only fields the dashboard actually reads.
export interface PageSnapshot {
  url: string;
  title: string;
  crawledAt: string;
  elements: UiElement[];
  ariaSnapshot?: string;
}

export interface UiElement {
  uaipId: string;
  tag: string;
  role?: string;
  accessibleName?: string;
  xpath: string;
  attrs: {
    id?: string;
    className?: string;
    dataAttributes?: Record<string, string>;
  };
  interactable?: boolean;
}

export type CrawlEdgeStatus =
  | "visited"
  | "queued"
  | "duplicate"
  | "out-of-scope"
  | "failed";

export interface CrawlEdge {
  fromUrl: string;
  toUrl: string;
  status: CrawlEdgeStatus;
  linkText?: string;
}

export type PageCategory =
  | "auth"
  | "commerce"
  | "content"
  | "account"
  | "shell"
  | "errors";

export interface PageLabel {
  label: string;
  category: PageCategory;
  confidence: number;
  ruleId: string;
}

export interface FlowNode {
  url: string;
  title: string;
  label: string;
  category: PageCategory;
  confidence: number;
}

export interface FlowEdge {
  fromUrl: string;
  toUrl: string;
  linkText?: string;
}

export interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface Journey {
  nodes: string[];
  labels: string[];
  score: number;
  name: string;
}

export interface CrawlResult {
  target: { url: string; viewport: { width: number; height: number } };
  pages: PageSnapshot[];
  edges?: CrawlEdge[];
  pageLabels?: Record<string, PageLabel[]>;
  flowGraph?: FlowGraph;
  journeys?: Journey[];
  durationMs: number;
}

export interface CrawlDetailResponse {
  summary: CrawlSummary;
  crawl: CrawlResult;
}

async function jsonGet<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  return (await res.json()) as T;
}

/**
 * Unpaginated crawls listing. Used by the Dashboard grid, which caps itself
 * at 12 cards for a quick-glance "what's live in this workspace" view; the
 * full paginated history lives on /runs via `listCrawlsPage`.
 */
export interface ListCrawlsOptions {
  /** Per-page cap on the returned `crawls[]` array. */
  limit?: number;
  /** "guest" → opt an authed user into the guest-workspace view (default
   *  tenancy + orphans). Useful when their own list is empty and we want
   *  to surface what's in the legacy public bucket. Anonymous visitors
   *  always get the guest view regardless of this param. */
  scope?: "guest";
}

export async function listCrawls(
  arg?: number | ListCrawlsOptions,
): Promise<CrawlSummary[]> {
  // Back-compat: callers passing a bare number still work.
  const opts: ListCrawlsOptions =
    typeof arg === "number" ? { limit: arg } : (arg ?? {});
  const qsParts: string[] = [];
  if (typeof opts.limit === "number") qsParts.push(`limit=${opts.limit}`);
  if (opts.scope === "guest") qsParts.push("scope=guest");
  const qs = qsParts.length > 0 ? `?${qsParts.join("&")}` : "";
  const data = await jsonGet<{ crawls: CrawlSummary[]; total: number }>(
    `/api/crawls${qs}`,
  );
  return data.crawls;
}

/**
 * Cheap "how many crawls would the guest workspace show" probe — used by
 * the Dashboard's empty state to decide whether to surface a "View as
 * guest" CTA. Returns just the count so the dashboard doesn't pay the
 * cost of materialising the full list when it only needs the number.
 */
export async function countGuestCrawls(): Promise<number> {
  const data = await jsonGet<{ total: number }>(`/api/crawls?scope=guest&limit=1`);
  return typeof data.total === "number" ? data.total : 0;
}

export interface CrawlListPage {
  crawls: CrawlSummary[];
  total: number;
  hasMore: boolean;
}

export interface CrawlListFilters {
  limit?: number;
  offset?: number;
  framework?: "java" | "python" | "unknown";
  authed?: boolean;
  q?: string;
}

/**
 * Paginated crawls listing (Phase 0 — task #98). Uses the `total` / `hasMore`
 * envelope so the Runs history page can page through large crawl sets without
 * loading every folder at once.
 */
export async function listCrawlsPage(
  filters: CrawlListFilters = {},
): Promise<CrawlListPage> {
  const params = new URLSearchParams();
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  if (filters.framework !== undefined) params.set("framework", filters.framework);
  if (filters.authed !== undefined) params.set("authed", String(filters.authed));
  if (filters.q) params.set("q", filters.q);
  const qs = params.toString();
  return jsonGet<CrawlListPage>(`/api/crawls${qs ? `?${qs}` : ""}`);
}

export async function getCrawl(id: string): Promise<CrawlDetailResponse> {
  return jsonGet<CrawlDetailResponse>(`/api/crawls/${encodeURIComponent(id)}`);
}

// ─────────────────────────── Intent tree ───────────────────────────

// These mirror @uaip/intent-tree. Duplicated rather than imported so the
// web app has no dependency on the Node-targeted package.

export type IntentId = PageCategory | "other";

export interface IntentPage {
  url: string;
  title: string;
  label: string;
  category: PageCategory;
  confidence: number;
}

export interface IntentFlow {
  name: string;
  score: number;
  labels: string[];
  pages: IntentPage[];
  solo: boolean;
}

export interface Intent {
  id: IntentId;
  title: string;
  description: string;
  category: PageCategory | null;
  flows: IntentFlow[];
}

export interface IntentTreeStats {
  intentCount: number;
  flowCount: number;
  pageCount: number;
  otherFlowCount: number;
}

export interface IntentTree {
  crawlId: string;
  targetUrl: string;
  intents: Intent[];
  stats: IntentTreeStats;
  generatedAt: string;
}

export async function getIntentTree(id: string): Promise<IntentTree> {
  const data = await jsonGet<{ tree: IntentTree }>(
    `/api/crawls/${encodeURIComponent(id)}/intents`,
  );
  return data.tree;
}

export async function listFiles(id: string): Promise<FileInfo[]> {
  const data = await jsonGet<{ files: FileInfo[] }>(
    `/api/crawls/${encodeURIComponent(id)}/files`,
  );
  return data.files;
}

export async function getFile(id: string, name: string): Promise<string> {
  const res = await fetch(
    `/api/crawls/${encodeURIComponent(id)}/files/${name
      .split("/")
      .map(encodeURIComponent)
      .join("/")}`,
  );
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.text();
}

/**
 * URL for the "Download ZIP" button on the Tests tab. Returned as a bare
 * string (not a fetch) so we can stick it on an <a href> and let the browser
 * handle the Content-Disposition attachment — no blob glue needed.
 */
export function downloadZipUrl(id: string): string {
  return `/api/crawls/${encodeURIComponent(id)}/files.zip`;
}

// ───────────────────────  Projects + Coverage  ───────────────────────

export interface ProjectSummary {
  id: string;
  name: string;
  createdAt: number;
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const res = await fetch("/api/projects");
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  const body = (await res.json()) as { projects?: ProjectSummary[] };
  return body.projects ?? [];
}

/**
 * DELETE /api/projects/:id — server cascades through crawls (FK CASCADE),
 * runs (project_id → NULL), project_memberships, AND the OutputStore
 * via store.deleteFolder for each crawl's outDir. Schedules survive
 * because they're URL-keyed, not project-keyed.
 *
 * Caller MUST confirm with the user before invoking — see
 * DeleteProjectDialog. This helper just wires the HTTP call.
 */
export async function deleteProject(id: string): Promise<void> {
  const res = await fetch(`/api/projects/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`${res.status}: ${await res.text()}`);
  }
}

/**
 * GET /api/projects/:id/delete-impact — pre-flight summary for the
 * confirm dialog. Tells the user how many crawls vanish and whether any
 * schedules will respawn the project on their next tick.
 *
 * Read-only; does not mutate or audit-log.
 */
export interface ProjectDeleteImpact {
  project: ProjectSummary;
  crawlCount: number;
  scheduleMatches: number;
  host: string | null;
}

export async function getProjectDeleteImpact(
  id: string,
): Promise<ProjectDeleteImpact> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(id)}/delete-impact`,
  );
  return jsonOrThrow(res);
}

export type CoverageStatus = "passed" | "failed" | "untested";

export interface CoveragePage {
  url: string;
  label: string;
  expectedClassName: string;
  status: CoverageStatus;
  passingTests: number;
  failingTests: number;
  isCritical: boolean;
  /** Tag set computed server-side from the page's label (Plan §21 Phase 4
   *  tag system). Always at least `["smoke"]`. */
  tags: string[];
}

export interface CoverageKpis {
  totalPages: number;
  testedPages: number;
  passingPages: number;
  failingPages: number;
  untestedPages: number;
  criticalTotal: number;
  criticalTested: number;
  pctPagesTested: number;
  pctCriticalFlowsTested: number;
  pctUntested: number;
}

export interface CoverageReportResponse {
  report: {
    pages: CoveragePage[];
    kpis: CoverageKpis;
    windowDays?: number;
    runCount?: number;
    computedAt?: string;
  };
  context: {
    crawlId: string | null;
    crawlAt: number | null;
    windowDays: number;
    runCount: number;
    traceCount: number;
    computedAt: string;
  };
  /** Plan Journey-5 SVG overlay payload. Always present; nodes=[] when
   *  no crawl was found or pageLabels weren't recognised by the rule
   *  registry. */
  flowGraph: FlowGraph;
}

export async function getCoverage(
  projectId: string,
  daysOpt?: number,
): Promise<CoverageReportResponse> {
  const qs = typeof daysOpt === "number" ? `?days=${daysOpt}` : "";
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/coverage${qs}`,
  );
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return (await res.json()) as CoverageReportResponse;
}

/**
 * URL for the PDF export. Returned as a bare string (not a fetch) so we can
 * stick it on an <a href download> and let the browser handle the
 * Content-Disposition attachment — no Blob plumbing needed.
 */
export function coveragePdfUrl(projectId: string, daysOpt?: number): string {
  const qs = typeof daysOpt === "number" ? `?days=${daysOpt}` : "";
  return `/api/projects/${encodeURIComponent(projectId)}/coverage.pdf${qs}`;
}

// ─────────────  Test runs spawned from the UI (Plan §16 Tier 2)  ─────────────

export interface RunStartResponse {
  run: {
    id: string;
    outDir: string;
    status: "running" | "done" | "failed" | "cancelled";
  };
}

/**
 * POST /api/crawls/:id/run — kicks off `uaip run --trace` server-side.
 * Returns the run row immediately; status polling is the caller's job.
 *
 * NOTE: we deliberately do NOT set `Content-Type: application/json` here.
 * The route takes no body — the crawl id comes from the URL path. Fastify's
 * default JSON body parser rejects empty bodies when the content-type
 * advertises JSON (FST_ERR_CTP_EMPTY_JSON_BODY → 400), and a content-type
 * with no body is semantically wrong anyway.
 */
export async function runTestsForCrawl(crawlId: string): Promise<RunStartResponse> {
  const res = await fetch(
    `/api/crawls/${encodeURIComponent(crawlId)}/run`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return (await res.json()) as RunStartResponse;
}

export interface RunStatusResponse {
  run?: {
    id: string;
    status: "running" | "done" | "failed" | "cancelled";
    exitCode: number | null;
    outDir: string;
  };
}

/** Polls /api/runs/:id every `intervalMs` until terminal status. */
export async function waitForRunFinish(
  runId: string,
  opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<RunStatusResponse["run"]> {
  const interval = opts.intervalMs ?? 1000;
  const deadline = Date.now() + (opts.timeoutMs ?? 10 * 60 * 1000);
  // Loop guard so a hung server doesn't pin the browser tab forever.
  while (Date.now() < deadline) {
    const res = await fetch(`/api/runs/${encodeURIComponent(runId)}`);
    if (res.ok) {
      const body = (await res.json()) as RunStatusResponse;
      const r = body.run;
      if (r && r.status !== "running") return r;
    }
    await new Promise((resolve) => setTimeout(resolve, interval));
  }
  throw new Error(`run ${runId} did not finish within timeout`);
}

// ─────────────────────────────  Auth  ─────────────────────────────

export type UserPlan = "free" | "starter" | "pro" | "team" | "enterprise" | "admin";
export type AuthProviderId = "google" | "github";
export type AuthMode = "off" | "optional" | "required";

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  plan: UserPlan;
  authProvider: AuthProviderId | null;
}

export interface Entitlements {
  plan: UserPlan | "anonymous";
  crawlsPerDay: number;
  crawlsPerMonth: number;
  maxPagesPerCrawl: number;
  maxTargetHosts: number;
  allowSemantic: boolean;
  allowHud: boolean;
  allowAuthedCrawl: boolean;
  /**
   * Emitters this plan may request on POST /api/runs. Literal `"*"` means no
   * restriction (Pro+). Kebab-case values mirror the CLI's `--emit` flag.
   * Keep in sync with `Framework` in `apps/api/src/billing/tiers.ts`.
   */
  allowedEmits: ReadonlyArray<
    | "*"
    | "playwright-ts"
    | "playwright-java"
    | "playwright-python"
    | "selenium-java"
    | "selenium-python"
    | "cypress-ts"
    | "uipath"
  >;
  /**
   * Max concurrent "projects" (= target web apps) this plan may own.
   * `0` (or any ≤0) means unlimited — used for Enterprise and admin.
   * Always set server-side from the tier catalog (with per-user override
   * resolved on top). Declared optional here only so we don't have to
   * hydrate it in the anon fallback constant.
   */
  maxProjects?: number;
  /**
   * Current project count for the signed-in user, stamped server-side onto
   * /api/auth/me so the dashboard can render "N / maxProjects" without a
   * second round trip. Absent for anonymous.
   */
  projectCount?: number;
  /**
   * Integration channels unlocked at this tier (Plan §21 Phase 4).
   *   Free + Starter: none / "slack"
   *   Pro+:           "slack" + "jira" + "linear"
   * Used client-side to hide integration panels + per-heal-issue buttons
   * the user can't actually invoke. Server still 402's defensively.
   * Optional for back-compat with cached /auth/me payloads from before
   * this field was added.
   */
  integrations?: ReadonlyArray<"slack" | "jira" | "linear">;
  /**
   * Team collaboration unlock (Plan §21 Phase 4 + 5):
   *   Free / Starter / Pro: false → "members" button + heal CollabPanel
   *                                  hidden from the dashboard.
   *   Team / Enterprise:    true  → membership management + heal
   *                                  assignee/comments visible.
   * Server still 402's any direct API call defensively. Optional for
   * back-compat with /auth/me payloads cached from before this field
   * was added — treat absent as `false` (the safer default).
   */
  teamCollaboration?: boolean;
}

/**
 * Phase 2 — subscription snapshot. `null` when the user has never
 * touched Stripe (no customer record yet → nothing to manage).
 * Mirrors what apps/api/src/auth/routes.ts `subscriptionSummary`
 * returns.
 */
export interface SubscriptionSummary {
  stripeCustomerId: string;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  status: string | null; // Stripe sub status or null pre-checkout
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  paymentFailedAt: number | null;
  /** Shows "Manage billing" when true, "Upgrade" otherwise. */
  isActive: boolean;
  /** Triggers the past-due/payment-failed banner on the account page. */
  needsAttention: boolean;
}

/**
 * Phase 2 — monthly usage snapshot. `null` on admin / enterprise tiers
 * where monthly metering isn't meaningful.
 */
export interface UsageSummary {
  /** UTC YYYY-MM string — cosmetic; the progress bar doesn't rely on this. */
  bucketMonth: string;
  used: number;
  /** 0 = unlimited (admin/enterprise never reach here because UsageSummary is null). */
  included: number;
  /** Extra crawls billed via Stripe meter events — pro only. */
  overage: number;
  /** 0..∞. >100 indicates overage territory. */
  percentOfIncluded: number;
}

export interface AuthMeResponse {
  user: AuthUser | null;
  entitlements: Entitlements;
  subscription: SubscriptionSummary | null;
  usage: UsageSummary | null;
  /**
   * True when the server has `UAIP_DEV_PLAN_OVERRIDE=1` set. The UI uses
   * this to render the dev-only plan switcher on the Account page. Always
   * false in production builds — see apps/api/src/dev/routes.ts.
   */
  devPlanOverrideEnabled?: boolean;
}

export interface AuthProvidersResponse {
  mode: AuthMode;
  providers: { id: AuthProviderId; label: string }[];
}

/** Returns `null` if the API has no auth configured (pre-Phase-1 servers). */
export async function getAuthMe(): Promise<AuthMeResponse | null> {
  const res = await fetch("/api/auth/me");
  if (res.status === 404) return null; // API too old
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return (await res.json()) as AuthMeResponse;
}

export async function getAuthProviders(): Promise<AuthProvidersResponse | null> {
  const res = await fetch("/api/auth/providers");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return (await res.json()) as AuthProvidersResponse;
}

export async function postLogout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}

export function signInUrl(provider: AuthProviderId, redirect = "/"): string {
  return `/api/auth/${provider}/start?redirect=${encodeURIComponent(redirect)}`;
}

// ────────────────────────── Dev plan override ─────────────────────────
//
// Only routed when the API has `UAIP_DEV_PLAN_OVERRIDE=1`. Production
// builds always get 404 → the UI hides the switcher based on
// `AuthMeResponse.devPlanOverrideEnabled`.

export type DevPlan = "free" | "starter" | "pro" | "team" | "enterprise";

export async function postDevPlan(plan: DevPlan): Promise<{ ok: true; plan: DevPlan }> {
  const res = await fetch("/api/dev/plan", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as { ok: true; plan: DevPlan };
}

// ────────────────────────── Billing (Phase 2) ──────────────────────────

/**
 * Stable error codes returned by POST /api/billing/{checkout,portal}.
 * The UI switches on these to decide between "retry later" vs
 * "this deployment doesn't have billing" vs "sign in first".
 */
export type BillingErrorCode =
  | "BILLING_DISABLED"
  | "AUTH_REQUIRED"
  | "ALREADY_SUBSCRIBED"
  | "NO_STRIPE_CUSTOMER"
  | "BILLING_CONFIG_INCOMPLETE"
  | "STRIPE_UPSTREAM_ERROR";

export class BillingError extends Error {
  constructor(public code: BillingErrorCode, message: string) {
    super(message);
  }
}

interface CheckoutResponse {
  url: string;
  id: string;
}
interface PortalResponse {
  url: string;
}

async function postBilling<T>(path: string, body?: unknown): Promise<T> {
  // Build init conditionally so we never pass `body: undefined` — under
  // `exactOptionalPropertyTypes`, `RequestInit.body` does not include
  // `undefined`, so the field has to be omitted entirely when absent.
  const init: RequestInit = {
    method: "POST",
    headers: { "content-type": "application/json" },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  if (res.ok) {
    return (await res.json()) as T;
  }
  const errBody = (await res.json().catch(() => ({}))) as { error?: string; code?: BillingErrorCode };
  throw new BillingError(errBody.code ?? "STRIPE_UPSTREAM_ERROR", errBody.error ?? `HTTP ${res.status}`);
}

/**
 * Kick off a Stripe Checkout session. Returns the hosted URL — callers
 * should `window.location.assign(url)` to redirect the browser.
 *
 * `tier` picks which recurring price to subscribe to. Defaults to "pro"
 * for back-compat with the original Phase 2 button. The server rejects
 * "starter" if `STRIPE_STARTER_PRICE_ID` is unset.
 */
export async function postBillingCheckout(
  tier: "starter" | "pro" | "team" = "pro",
  /** Required for `tier="team"` — number of seats to subscribe to (Stripe
   *  will clamp to the server-side minimum if smaller). Ignored for the
   *  other tiers. */
  seats?: number,
): Promise<CheckoutResponse> {
  return postBilling<CheckoutResponse>("/api/billing/checkout", {
    tier,
    ...(tier === "team" && typeof seats === "number" ? { seats } : {}),
  });
}

/**
 * Open the Stripe-hosted Billing Portal. Callers redirect to the
 * returned URL; the portal bounces back to /account when the user is
 * done.
 */
export async function postBillingPortal(): Promise<PortalResponse> {
  return postBilling<PortalResponse>("/api/billing/portal");
}

// ────────────────────────── Schedules (Phase 0) ──────────────────────────

/**
 * One row in `scheduled_crawls` as returned by /api/schedules. Kept in
 * sync with apps/api/src/schedules.ts `PublicSchedule`.
 */
export interface Schedule {
  id: string;
  userId: string;
  name: string;
  targetUrl: string;
  crawlOptions: Record<string, unknown>;
  cronExpression: string;
  timezone: string;
  enabled: boolean;
  lastRunAt: number | null;
  nextRunAt: number | null;
  createdAt: number;
  updatedAt: number | null;
}

export interface ScheduleCreateInput {
  name: string;
  targetUrl: string;
  cronExpression: string;
  timezone?: string;
  enabled?: boolean;
  crawlOptions?: Record<string, unknown>;
}

export type ScheduleUpdateInput = Partial<ScheduleCreateInput>;

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function listSchedules(): Promise<Schedule[]> {
  const res = await fetch("/api/schedules");
  const data = await jsonOrThrow<{ schedules: Schedule[] }>(res);
  return data.schedules;
}

export async function createSchedule(input: ScheduleCreateInput): Promise<Schedule> {
  const res = await fetch("/api/schedules", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ schedule: Schedule }>(res);
  return data.schedule;
}

export async function updateSchedule(id: string, patch: ScheduleUpdateInput): Promise<Schedule> {
  const res = await fetch(`/api/schedules/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  const data = await jsonOrThrow<{ schedule: Schedule }>(res);
  return data.schedule;
}

export async function deleteSchedule(id: string): Promise<void> {
  const res = await fetch(`/api/schedules/${encodeURIComponent(id)}`, { method: "DELETE" });
  await jsonOrThrow<{ ok: true }>(res);
}

export async function pauseSchedule(id: string): Promise<Schedule> {
  const res = await fetch(`/api/schedules/${encodeURIComponent(id)}/pause`, { method: "POST" });
  const data = await jsonOrThrow<{ schedule: Schedule }>(res);
  return data.schedule;
}

export async function resumeSchedule(id: string): Promise<Schedule> {
  const res = await fetch(`/api/schedules/${encodeURIComponent(id)}/resume`, { method: "POST" });
  const data = await jsonOrThrow<{ schedule: Schedule }>(res);
  return data.schedule;
}

// ────────────────────────── Notifications (Phase 0) ──────────────────────────

export type NotificationChannel = "slack";
export type NotificationEvent = "run.completed" | "run.failed" | "schedule.fired";

/** Mirrors apps/api/src/notifications/routes.ts `PublicNotificationConfig`. */
export interface NotificationConfig {
  id: string;
  userId: string;
  channel: NotificationChannel;
  webhookUrl: string;
  label: string | null;
  events: NotificationEvent[];
  enabled: boolean;
  createdAt: number;
  updatedAt: number | null;
}

export interface NotificationDelivery {
  id: string;
  configId: string;
  userId: string;
  event: NotificationEvent;
  status: "ok" | "error";
  httpStatus: number | null;
  errorMessage: string | null;
  deliveredAt: number;
}

export interface NotificationConfigCreateInput {
  channel: NotificationChannel;
  webhookUrl: string;
  label?: string | null;
  events?: NotificationEvent[];
  enabled?: boolean;
}

export type NotificationConfigUpdateInput = Partial<NotificationConfigCreateInput>;

export async function listNotificationConfigs(): Promise<NotificationConfig[]> {
  const res = await fetch("/api/notifications");
  const data = await jsonOrThrow<{ configs: NotificationConfig[] }>(res);
  return data.configs;
}

export async function getNotificationConfig(
  id: string,
): Promise<{ config: NotificationConfig; deliveries: NotificationDelivery[] }> {
  const res = await fetch(`/api/notifications/${encodeURIComponent(id)}`);
  return jsonOrThrow<{ config: NotificationConfig; deliveries: NotificationDelivery[] }>(res);
}

export async function createNotificationConfig(
  input: NotificationConfigCreateInput,
): Promise<NotificationConfig> {
  const res = await fetch("/api/notifications", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ config: NotificationConfig }>(res);
  return data.config;
}

export async function updateNotificationConfig(
  id: string,
  patch: NotificationConfigUpdateInput,
): Promise<NotificationConfig> {
  const res = await fetch(`/api/notifications/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  const data = await jsonOrThrow<{ config: NotificationConfig }>(res);
  return data.config;
}

export async function deleteNotificationConfig(id: string): Promise<void> {
  const res = await fetch(`/api/notifications/${encodeURIComponent(id)}`, { method: "DELETE" });
  await jsonOrThrow<{ ok: true }>(res);
}

export async function testNotificationConfig(
  id: string,
): Promise<{ delivered: NotificationDelivery | null }> {
  const res = await fetch(`/api/notifications/${encodeURIComponent(id)}/test`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: "{}",
  });
  return jsonOrThrow<{ delivered: NotificationDelivery | null }>(res);
}

export async function listNotificationDeliveries(
  limit = 50,
): Promise<NotificationDelivery[]> {
  const res = await fetch(`/api/notifications/deliveries?limit=${limit}`);
  const data = await jsonOrThrow<{ deliveries: NotificationDelivery[] }>(res);
  return data.deliveries;
}

// ───────── Run traces ─────────
//
// The trace viewer talks to /api/runs/:id/trace. The manifest is a UAIP
// trace (see @uaip/trace.UaipTrace) — we use `Record<string, unknown>`
// in the web layer rather than pulling in the trace package to keep the
// web bundle small; callers that care about specific fields narrow it
// themselves.

export type RunTraceStatus = "passed" | "failed" | "errored";

export interface RunTraceSummary {
  runId: string;
  userId: string;
  uploadedAt: string; // ISO-8601
  status: RunTraceStatus;
  framework: string;
  runner: string;
  durationMs: number;
  testCount: number;
  /** True iff a live (non-expired) share token exists for this trace. */
  isShared?: boolean;
}

export interface RunTrace extends RunTraceSummary {
  manifest: Record<string, unknown>;
}

/**
 * Public-mode projection (no userId, but with share metadata). Returned by
 * `getSharedTraceByToken` and consumed by the public viewer.
 */
export interface SharedRunTrace {
  runId: string;
  uploadedAt: string;
  status: RunTraceStatus;
  framework: string;
  runner: string;
  durationMs: number;
  testCount: number;
  manifest: Record<string, unknown>;
  shareCreatedAt: string;
  shareExpiresAt: string | null;
}

export interface ShareInfo {
  runId: string;
  shareUrl: string;
  shareToken: string;
  shareCreatedAt: string;
  shareExpiresAt: string | null;
}

export async function listRecentTraces(limit = 50): Promise<RunTraceSummary[]> {
  const res = await fetch(`/api/traces?limit=${limit}`);
  const data = await jsonOrThrow<{ traces: RunTraceSummary[] }>(res);
  return data.traces;
}

export async function getRunTrace(runId: string): Promise<RunTrace> {
  const res = await fetch(`/api/runs/${encodeURIComponent(runId)}/trace`);
  const data = await jsonOrThrow<{ trace: RunTrace }>(res);
  return data.trace;
}

export async function getRunTraceSummary(
  runId: string,
): Promise<RunTraceSummary> {
  const res = await fetch(
    `/api/runs/${encodeURIComponent(runId)}/trace/summary`,
  );
  const data = await jsonOrThrow<{ trace: RunTraceSummary }>(res);
  return data.trace;
}

export async function uploadRunTrace(
  runId: string,
  manifest: Record<string, unknown>,
): Promise<RunTraceSummary> {
  const res = await fetch(`/api/runs/${encodeURIComponent(runId)}/trace`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(manifest),
  });
  const data = await jsonOrThrow<{ trace: RunTraceSummary }>(res);
  return data.trace;
}

/**
 * Mint or rotate a share token for a trace. Calling on an already-shared
 * trace ROTATES the token: the previous URL stops working. The UI should
 * confirm before calling this when `summary.isShared === true`.
 *
 * `ttlHours` is optional — omit / pass <=0 for a non-expiring share.
 */
export async function createTraceShare(
  runId: string,
  opts: { ttlHours?: number } = {},
): Promise<ShareInfo> {
  const body: Record<string, unknown> = {};
  if (typeof opts.ttlHours === "number" && opts.ttlHours > 0) {
    body.ttlHours = opts.ttlHours;
  }
  const res = await fetch(`/api/runs/${encodeURIComponent(runId)}/share`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await jsonOrThrow<{ share: ShareInfo }>(res);
  return data.share;
}

/** Revoke a trace's share token. Idempotent. */
export async function deleteTraceShare(runId: string): Promise<void> {
  const res = await fetch(`/api/runs/${encodeURIComponent(runId)}/share`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `share revoke failed: ${res.status}`);
  }
}

/**
 * Fetch a shared trace by its token. NO authentication required — this is
 * the read path the public viewer uses. Throws on 404 (token unknown or
 * expired).
 */
export async function getSharedTraceByToken(
  token: string,
): Promise<SharedRunTrace> {
  const res = await fetch(`/api/public/traces/${encodeURIComponent(token)}`);
  const data = await jsonOrThrow<{ trace: SharedRunTrace }>(res);
  return data.trace;
}

// ────────────────────────── GitHub App (Phase 1) ──────────────────────────
//
// Mirrors what apps/api/src/github/routes.ts returns. The "Send as PR"
// feature is gated on having at least one linked repo.

export interface GitHubLinkedRepo {
  id: string;
  userId: string;
  installationId: string;
  repoId: string;
  owner: string;
  name: string;
  fullName: string;
  defaultBranch: string;
  privateRepo: boolean;
  lastPrUrl: string | null;
  lastPrAt: number | null;
}

export interface GitHubInstallationView {
  installationId: string;
  accountLogin: string;
  accountType: string;
  repositorySelection: string;
  permissions: string | null;
  linkedRepos: GitHubLinkedRepo[];
  createdAt: number;
  updatedAt: number | null;
}

export interface GitHubRepoPickerItem {
  repoId: string;
  name: string;
  fullName: string;
  owner: string;
  private: boolean;
  defaultBranch: string;
}

export interface OpenPrResponse {
  ok: true;
  pr: {
    number: number;
    htmlUrl: string;
    branch: string;
    commitSha: string;
    reusedExistingPr: boolean;
  };
}

export interface OpenPrFile {
  path: string;
  content: string;
}

export class GitHubApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
  }
}

async function githubJsonOrThrow<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  throw new GitHubApiError(
    res.status,
    body.code ?? null,
    body.error ?? `HTTP ${res.status}`,
  );
}

export async function listGitHubInstallations(): Promise<GitHubInstallationView[]> {
  const res = await fetch("/api/github/installations");
  const data = await githubJsonOrThrow<{ installations: GitHubInstallationView[] }>(res);
  return data.installations;
}

export async function listGitHubRepos(
  installationId: string,
  page = 1,
  perPage = 30,
): Promise<{ totalCount: number; repositories: GitHubRepoPickerItem[] }> {
  const res = await fetch(
    `/api/github/installations/${encodeURIComponent(installationId)}/repos?page=${page}&per_page=${perPage}`,
  );
  return githubJsonOrThrow(res);
}

export async function linkGitHubRepo(
  installationId: string,
  input: {
    repoId: string;
    owner: string;
    name: string;
    fullName: string;
    defaultBranch?: string;
    privateRepo?: boolean;
  },
): Promise<GitHubLinkedRepo> {
  const res = await fetch(
    `/api/github/installations/${encodeURIComponent(installationId)}/link`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  const data = await githubJsonOrThrow<{ linked: GitHubLinkedRepo }>(res);
  return data.linked;
}

export async function unlinkGitHubRepo(linkedId: string): Promise<void> {
  const res = await fetch(
    `/api/github/linked-repos/${encodeURIComponent(linkedId)}/unlink`,
    { method: "POST" },
  );
  await githubJsonOrThrow<{ ok: true }>(res);
}

export async function deleteGitHubInstallation(
  installationId: string,
): Promise<{ ok: true; revokeUrl: string }> {
  const res = await fetch(
    `/api/github/installations/${encodeURIComponent(installationId)}`,
    { method: "DELETE" },
  );
  return githubJsonOrThrow(res);
}

export async function openGitHubPr(
  linkedId: string,
  input: {
    title: string;
    body?: string;
    branch?: string;
    commitMessage?: string;
    files: OpenPrFile[];
    forceUpdateBranch?: boolean;
  },
): Promise<OpenPrResponse> {
  const res = await fetch(
    `/api/github/linked-repos/${encodeURIComponent(linkedId)}/pr`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return githubJsonOrThrow(res);
}

export async function deleteRunTrace(runId: string): Promise<void> {
  const res = await fetch(`/api/runs/${encodeURIComponent(runId)}/trace`, {
    method: "DELETE",
  });
  if (!res.ok) {
    // DELETE returns 204 on success; jsonOrThrow's res.json() would
    // choke on an empty body, so we only parse when the server is
    // actually returning an error payload.
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
}

// ────────────────────── Self-healing (Phase 3A) ──────────────────────
//
// Types mirror apps/api/src/heals/routes.ts `toPublicSummary` +
// `toPublicDecision`. The `manifest` field on detail is the raw
// HealReport JSON (healVersion=1); we model it loosely here because
// the web app only reads a small subset of proposal fields.

export type RunHealStatus =
  | "proposed"
  | "in-review"
  | "partially-accepted"
  | "accepted"
  | "pr-opened"
  | "merged"
  | "discarded";
export type RunHealSource = "cli" | "dashboard" | "scheduled";
export type RunHealDecisionKind = "accept" | "reject" | "override";
export type HealMatchKind = "exact" | "soft" | "missing";

export interface RunHealSummary {
  id: string;
  userId: string;
  runId: string | null;
  previousCrawlId: string | null;
  newCrawlId: string | null;
  createdAt: string;
  updatedAt: string | null;
  status: RunHealStatus;
  source: RunHealSource;
  totalCount: number;
  exactCount: number;
  softCount: number;
  missingCount: number;
  meanSoftConfidence: number;
  prUrl: string | null;
  prOpenedAt: string | null;
  /** Plan §21 Phase 4 team workflow v1 — assignee for review. */
  assigneeUserId: string | null;
}

/**
 * Minimum shape of a HealProposal as consumed by the review UI. The
 * server stores the full @uaip/heal HealProposal inside `manifest.proposals`
 * but the dashboard only reads these fields — keep the type loose so
 * upstream spec bumps don't require a web rebuild.
 */
export interface HealProposalSlim {
  previousUaipId: string;
  previousPageUrl?: string;
  previousAccessibleName?: string;
  newUaipId?: string;
  newPageUrl?: string;
  newAccessibleName?: string;
  matchKind: HealMatchKind;
  matchConfidence: number;
  matchReason: string;
}

export interface HealDecision {
  healId: string;
  previousUaipId: string;
  decision: RunHealDecisionKind;
  newUaipId: string | null;
  note: string | null;
  createdAt: string;
}

export interface RunHealDetail extends RunHealSummary {
  manifest: {
    healVersion?: number;
    generatedAt?: string;
    previousPath?: string;
    currentPath?: string;
    options?: Record<string, unknown>;
    summary?: {
      total: number;
      exact: number;
      soft: number;
      missing: number;
      meanSoftConfidence: number;
    };
    proposals?: HealProposalSlim[];
    [k: string]: unknown;
  };
  decisions: HealDecision[];
}

export async function listHeals(limit = 50): Promise<RunHealSummary[]> {
  const res = await fetch(`/api/heals?limit=${limit}`);
  const data = await jsonOrThrow<{ heals: RunHealSummary[] }>(res);
  return data.heals;
}

export async function listHealsForRun(runId: string): Promise<RunHealSummary[]> {
  const res = await fetch(
    `/api/runs/${encodeURIComponent(runId)}/heals`,
  );
  const data = await jsonOrThrow<{ heals: RunHealSummary[] }>(res);
  return data.heals;
}

/**
 * Generate a heal report by diffing two crawls of the same project. The
 * server reads both crawl.json blobs via the OutputStore (works on
 * local + GCS), runs the @uaip/heal pipeline, and persists a row of
 * the same shape as a CLI `uaip heal --upload` would create.
 *
 * Used by the "Compare with previous crawl" button on the Crawl Detail
 * page. Returns the new heal summary so the caller can navigate to
 * `/heals/<id>` for review.
 *
 * Tier-gated server-side: heal feature must be enabled (Pro+).
 */
export async function generateHeal(input: {
  previousOutDir: string;
  currentOutDir: string;
}): Promise<RunHealSummary> {
  const res = await fetch("/api/heals/generate", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ heal: RunHealSummary }>(res);
  return data.heal;
}

// ─────────────── Auth-state vault (Plan §22 Login UX) ───────────────
//
// Encrypted at-rest storageState blobs the crawler injects to walk
// authenticated sites. The web UI never sees the encrypted blob —
// only the public summary (id, label, host, source, timestamps).

export type AuthStateSource = "recipe" | "vnc" | "extension" | "upload";

export interface AuthStateSummary {
  id: string;
  label: string;
  host: string;
  source: AuthStateSource;
  projectId: string | null;
  /** True when the vault has the recipe stored (remember-credentials). */
  canAutoRefresh: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

export async function listAuthStates(opts?: {
  host?: string;
}): Promise<AuthStateSummary[]> {
  const qs = opts?.host ? `?host=${encodeURIComponent(opts.host)}` : "";
  const res = await fetch(`/api/auth-states${qs}`);
  const data = await jsonOrThrow<{ authStates: AuthStateSummary[] }>(res);
  return data.authStates;
}

export interface CaptureAuthStateInput {
  label: string;
  loginUrl: string;
  usernameSelector: string;
  passwordSelector: string;
  submitSelector: string;
  postLoginUrlContains?: string;
  username: string;
  password: string;
  /** Persist the recipe + creds so we can auto-refresh on cookie expiry.
   *  Default false → use-once: creds are zeroed after the run. */
  remember?: boolean;
  projectId?: string | null;
}

export async function captureAuthState(
  input: CaptureAuthStateInput,
): Promise<AuthStateSummary> {
  const res = await fetch("/api/auth-states", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ authState: AuthStateSummary }>(res);
  return data.authState;
}

export async function deleteAuthState(id: string): Promise<void> {
  const res = await fetch(`/api/auth-states/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`${res.status}: ${await res.text()}`);
  }
}

/** Auto-detected login-form selector. Drives the picker pre-fill. */
export type LoginSelectorKind =
  | "id"
  | "name"
  | "type"
  | "role"
  | "text"
  | "css"
  | "xpath";
export interface LoginSelectorSlot {
  value: string;
  confidence: number;
  kind: LoginSelectorKind;
  alternates: { value: string; kind: LoginSelectorKind }[];
}
export interface LoginFormDetection {
  found: boolean;
  username: LoginSelectorSlot;
  password: LoginSelectorSlot;
  submit: LoginSelectorSlot;
  pageTitle: string;
}

/** Probe a login URL with headless Playwright, get back ranked
 *  selector candidates. Used by the AuthStateCaptureDialog "Auto-detect
 *  form fields" button so QA users don't have to write CSS/XPath. */
export async function detectLoginForm(input: {
  loginUrl: string;
}): Promise<LoginFormDetection> {
  const res = await fetch("/api/auth-states/detect-form", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ detection: LoginFormDetection }>(res);
  return data.detection;
}

export async function getHeal(id: string): Promise<RunHealDetail> {
  const res = await fetch(`/api/heals/${encodeURIComponent(id)}`);
  const data = await jsonOrThrow<{ heal: RunHealDetail }>(res);
  return data.heal;
}

export async function patchHealStatus(
  id: string,
  status: RunHealStatus,
): Promise<RunHealSummary> {
  const res = await fetch(`/api/heals/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ status }),
  });
  const data = await jsonOrThrow<{ heal: RunHealSummary }>(res);
  return data.heal;
}

export async function deleteHeal(id: string): Promise<void> {
  const res = await fetch(`/api/heals/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
}

export interface UpsertHealDecisionInput {
  previousUaipId: string;
  decision: RunHealDecisionKind;
  newUaipId?: string | null;
  note?: string | null;
}

/**
 * Response carries the decision row *and* the recomputed status so the
 * dashboard can update its badge without a second round-trip. Mirrors
 * what POST /api/heals/:id/decisions returns.
 */
export interface UpsertHealDecisionResult {
  decision: HealDecision;
  healStatus: RunHealStatus;
}

export async function upsertHealDecision(
  healId: string,
  input: UpsertHealDecisionInput,
): Promise<UpsertHealDecisionResult> {
  const res = await fetch(
    `/api/heals/${encodeURIComponent(healId)}/decisions`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return jsonOrThrow<UpsertHealDecisionResult>(res);
}

export async function deleteHealDecision(
  healId: string,
  previousUaipId: string,
): Promise<void> {
  const res = await fetch(
    `/api/heals/${encodeURIComponent(healId)}/decisions/${encodeURIComponent(previousUaipId)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
}

export interface OpenHealPrInput {
  linkedRepoId: string;
  title?: string;
  body?: string;
  branch?: string;
  commitMessage?: string;
  forceUpdateBranch?: boolean;
}

/**
 * Opens a GitHub PR carrying the two-file heal changeset (summary markdown
 * + machine-applicable JSON). On success the server also flips the heal to
 * `pr-opened` and stamps `prUrl`, so callers should refresh the heal row.
 *
 * Errors surface as `GitHubApiError` so callers can branch on `.code`
 * (HEAL_INCOMPLETE / HEAL_DISCARDED / GITHUB_BRANCH_EXISTS / …) — same
 * contract as `openGitHubPr`.
 */
export async function openHealPr(
  healId: string,
  input: OpenHealPrInput,
): Promise<OpenPrResponse> {
  const res = await fetch(
    `/api/heals/${encodeURIComponent(healId)}/pr`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return githubJsonOrThrow(res);
}

// ─────────── Linear integration (Plan §21 Phase 4) ───────────

export interface LinearTeam {
  id: string;
  name: string;
  key: string; // short prefix Linear puts on issue ids ("ENG")
}

export interface LinearInstallationView {
  workspaceId: string;
  workspaceName: string;
  workspaceUrlKey: string | null;
  defaultTeamId: string | null;
  defaultTeamName: string | null;
  defaultTeamKey: string | null;
  /** Only populated by /connect and /teams/refresh; null on plain GET. */
  teams: LinearTeam[] | null;
  createdAt: number;
  updatedAt: number | null;
}

export interface CreatedLinearIssue {
  id: string;
  identifier: string; // "ENG-42"
  title: string;
  url: string;
}

export class LinearApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
  }
}

async function linearJsonOrThrow<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  throw new LinearApiError(
    res.status,
    body.code ?? null,
    body.error ?? `HTTP ${res.status}`,
  );
}

export async function getLinearInstallation(): Promise<LinearInstallationView | null> {
  const res = await fetch("/api/integrations/linear");
  const data = await linearJsonOrThrow<{ installation: LinearInstallationView | null }>(
    res,
  );
  return data.installation;
}

export async function connectLinear(
  apiKey: string,
  teamId?: string,
): Promise<LinearInstallationView> {
  const res = await fetch("/api/integrations/linear/connect", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ apiKey, ...(teamId ? { teamId } : {}) }),
  });
  const data = await linearJsonOrThrow<{ installation: LinearInstallationView }>(res);
  return data.installation;
}

export async function refreshLinearTeams(): Promise<LinearInstallationView> {
  const res = await fetch("/api/integrations/linear/teams/refresh", {
    method: "POST",
  });
  const data = await linearJsonOrThrow<{ installation: LinearInstallationView }>(res);
  return data.installation;
}

export async function disconnectLinear(): Promise<void> {
  const res = await fetch("/api/integrations/linear", { method: "DELETE" });
  await linearJsonOrThrow<{ ok: true }>(res);
}

export async function createLinearIssue(input: {
  title: string;
  description?: string;
  teamId?: string;
}): Promise<CreatedLinearIssue> {
  const res = await fetch("/api/integrations/linear/issue", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await linearJsonOrThrow<{ ok: true; issue: CreatedLinearIssue }>(res);
  return data.issue;
}

// ─────────── Jira integration (Plan §21 Phase 4) ───────────

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraInstallationView {
  siteUrl: string;
  siteName: string | null;
  accountId: string | null;
  displayName: string | null;
  defaultProjectKey: string | null;
  defaultProjectId: string | null;
  defaultProjectName: string | null;
  defaultIssueTypeName: string;
  /** Only populated by /connect and /projects/refresh; null on plain GET. */
  projects: JiraProject[] | null;
  createdAt: number;
  updatedAt: number | null;
}

export interface CreatedJiraIssue {
  id: string;
  key: string; // "ENG-42"
  url: string;
  summary: string;
}

export class JiraApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
  }
}

async function jiraJsonOrThrow<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  throw new JiraApiError(
    res.status,
    body.code ?? null,
    body.error ?? `HTTP ${res.status}`,
  );
}

export async function getJiraInstallation(): Promise<JiraInstallationView | null> {
  const res = await fetch("/api/integrations/jira");
  const data = await jiraJsonOrThrow<{ installation: JiraInstallationView | null }>(
    res,
  );
  return data.installation;
}

export async function connectJira(input: {
  email: string;
  apiToken: string;
  siteUrl: string;
  projectKey?: string;
}): Promise<JiraInstallationView> {
  const res = await fetch("/api/integrations/jira/connect", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jiraJsonOrThrow<{ installation: JiraInstallationView }>(res);
  return data.installation;
}

export async function refreshJiraProjects(): Promise<JiraInstallationView> {
  const res = await fetch("/api/integrations/jira/projects/refresh", {
    method: "POST",
  });
  const data = await jiraJsonOrThrow<{ installation: JiraInstallationView }>(res);
  return data.installation;
}

export async function disconnectJira(): Promise<void> {
  const res = await fetch("/api/integrations/jira", { method: "DELETE" });
  await jiraJsonOrThrow<{ ok: true }>(res);
}

export async function createJiraIssue(input: {
  summary: string;
  description?: string;
  projectKey?: string;
  issueType?: string;
}): Promise<CreatedJiraIssue> {
  const res = await fetch("/api/integrations/jira/issue", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jiraJsonOrThrow<{ ok: true; issue: CreatedJiraIssue }>(res);
  return data.issue;
}

// ─────────── Heal collaboration v1 (Plan §21 Phase 4) ───────────

export interface HealComment {
  id: string;
  healId: string;
  authorUserId: string | null;
  body: string;
  createdAt: string;
}

export async function listHealComments(healId: string): Promise<HealComment[]> {
  const res = await fetch(
    `/api/heals/${encodeURIComponent(healId)}/comments`,
  );
  const data = await jsonOrThrow<{ comments: HealComment[] }>(res);
  return data.comments;
}

export async function addHealComment(
  healId: string,
  body: string,
): Promise<HealComment> {
  const res = await fetch(
    `/api/heals/${encodeURIComponent(healId)}/comments`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ body }),
    },
  );
  const data = await jsonOrThrow<{ comment: HealComment }>(res);
  return data.comment;
}

export async function setHealAssignee(
  healId: string,
  assigneeUserId: string | null,
): Promise<RunHealSummary> {
  const res = await fetch(
    `/api/heals/${encodeURIComponent(healId)}/assignee`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ assigneeUserId }),
    },
  );
  const data = await jsonOrThrow<{ ok: true; heal: RunHealSummary }>(res);
  return data.heal;
}

// ─────────── Audit logs (Plan §21 Phase 5) ───────────

export interface AuditEvent {
  id: string;
  userId: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  ip: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export async function listAuditEvents(opts: {
  limit?: number;
  prefix?: string;
} = {}): Promise<AuditEvent[]> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.prefix) params.set("prefix", opts.prefix);
  const qs = params.toString();
  const res = await fetch(`/api/audit-logs${qs ? `?${qs}` : ""}`);
  const data = await jsonOrThrow<{ events: AuditEvent[] }>(res);
  return data.events;
}

// ─────────── RBAC v1 (Plan §21 Phase 5) ───────────

export type ProjectRole = "owner" | "admin" | "editor" | "viewer";

export interface ProjectMember {
  projectId: string;
  userId: string;
  role: ProjectRole;
  email: string | null;
  name: string | null;
  invitedBy: string | null;
  invitedAt: string;
  acceptedAt: string | null;
}

export async function listProjectMembers(
  projectId: string,
): Promise<{ members: ProjectMember[]; yourRole: ProjectRole }> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/members`,
  );
  return jsonOrThrow(res);
}

export async function addProjectMember(
  projectId: string,
  email: string,
  role: ProjectRole,
): Promise<ProjectMember> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/members`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, role }),
    },
  );
  const data = await jsonOrThrow<{ member: ProjectMember }>(res);
  return data.member;
}

export async function updateProjectMember(
  projectId: string,
  userId: string,
  role: ProjectRole,
): Promise<void> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ role }),
    },
  );
  await jsonOrThrow(res);
}

export async function removeProjectMember(
  projectId: string,
  userId: string,
): Promise<void> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
  await jsonOrThrow(res);
}

// ─────────── SSO v1 (Plan §21 Phase 5) ───────────

export type SsoIdpProvider =
  | "google-workspace"
  | "okta"
  | "entra"
  | "generic-saml";

export interface SsoConfiguration {
  domain: string;
  idpProvider: SsoIdpProvider;
  entityId: string;
  ssoUrl: string;
  x509CertPem: string;
  enabled: boolean;
  defaultPlan: string | null;
  createdAt: string;
  updatedAt: string | null;
  createdBy: string | null;
}

export interface SsoDiscovery {
  hasSso: boolean;
  idpProvider?: SsoIdpProvider;
  domain?: string;
}

/**
 * Public — used by the sign-in page before the user is authed.
 * Returns just enough for the UI to decide whether to show the
 * "Continue with SSO" button.
 */
export async function discoverSso(email: string): Promise<SsoDiscovery> {
  const res = await fetch(
    `/api/sso/discover?email=${encodeURIComponent(email)}`,
  );
  return jsonOrThrow(res);
}

export async function listSsoConfigurations(): Promise<SsoConfiguration[]> {
  const res = await fetch("/api/sso/configurations");
  const data = await jsonOrThrow<{ configurations: SsoConfiguration[] }>(res);
  return data.configurations;
}

export async function upsertSsoConfiguration(
  input: Partial<SsoConfiguration> & {
    domain: string;
    idpProvider: SsoIdpProvider;
  },
): Promise<SsoConfiguration> {
  const res = await fetch("/api/sso/configurations", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ configuration: SsoConfiguration }>(res);
  return data.configuration;
}

export async function deleteSsoConfiguration(domain: string): Promise<void> {
  const res = await fetch(
    `/api/sso/configurations/${encodeURIComponent(domain)}`,
    { method: "DELETE" },
  );
  await jsonOrThrow(res);
}

// ─────────────── Tenant identity (Plan §21 Phase 5) ───────────────
//
// The web client fetches /api/tenant once at boot to brand the top-nav.
// Multi-tenant deployments get the generic "UAIP" identity; single-tenant
// installs return the customer's name + (optional) https logo + footer
// contact email. Public on purpose — the tenant identity is on the front
// door of any deployment, not a secret.

export type TenancyMode = "multi-tenant" | "single-tenant";

export interface TenantIdentity {
  mode: TenancyMode;
  tenantId: string;
  tenantName: string;
  /** "" when unset. Always https:// when set (server-side validated). */
  logoUrl: string;
  /** "" when unset. */
  contactEmail: string;
}

export async function getTenant(): Promise<TenantIdentity> {
  const res = await fetch("/api/tenant");
  const data = await jsonOrThrow<{ tenant: TenantIdentity }>(res);
  return data.tenant;
}
